const API_URL = process.env.NODE_ENV === 'production' 
  ? '/api'  // Production: Railway serves both frontend and backend
  : 'http://localhost:5000/api';  // Development: Local backend

/**
 * Get all customers from backend
 * Backend returns: ["KENWORTH TRUCK COMPANY", "PETERBILT MOTORS CO."]
 * Frontend needs: [{id: 0, name: "KENWORTH..."}, {id: 1, name: "PETERBILT..."}]
 */
export const searchCustomers = async (search) => {
    const response = await fetch(`${API_URL}/customers`);
    
    if (!response.ok) {
        throw new Error('Failed to fetch customers');
    }
    
    const customerNames = await response.json();
    
    // Convert to format frontend expects
    const customers = customerNames.map((name, index) => ({
        id: index,
        name: name
    }));
    
    // Filter by search term if provided
    if (search) {
        return customers.filter(c => 
            c.name.toLowerCase().includes(search.toLowerCase())
        );
    }
    
    return customers;
};

// ============================================================================
// CHANGE 1: NEW FUNCTION - Extract year from any date format
// ============================================================================
/**
 * Extract year from various date formats
 * Handles: "MM/DD/YYYY HH:MM:SS", "YYYY-MM-DD", "YYYY-MM-DD HH:MM:SS"
 */
function extractYear(dateString) {
    if (!dateString) return null;
    
    // Try to find a 4-digit year pattern (19xx or 20xx)
    const yearMatch = dateString.match(/\b(19|20)\d{2}\b/);
    if (yearMatch) {
        return yearMatch[0];
    }
    
    return null;
}
// ============================================================================

/**
 * Search for VIN and get parts
 * New backend uses POST /api/search with { customer, vin, year }
 */
export const verifyVIN = async (customerName, vinNumber, year) => {
    const response = await fetch(`${API_URL}/search`, {
        method: 'POST',
        headers: { 
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
            customer: customerName,
            vin: vinNumber,
            year: year ? year.toString() : ''
        })
    });
    
    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'VIN not found');
    }
    
    const data = await response.json();
    
    // Multiple distinct records matched (e.g. year was left blank and this VIN
    // has several configs on file) - let the caller show a picker instead of guessing
    if (data.multipleMatches) {
        return {
            multipleMatches: true,
            options: data.options,
            customerName,
            vinNumber
        };
    }
    
    // ============================================================================
    // CHANGE 2: Updated return statement (2 small changes)
    // ============================================================================
    // Return in format frontend expects
    return {
        part_id: data.partId,
        model_year: extractYear(data.shippingDate),  // CHANGED: Use extractYear() instead of substring(0,4)
        parts: data.parts,
        vin: vinNumber  // ADDED: Pass VIN through for display
    };
    // ============================================================================
};

/**
 * Decode a specific Part ID directly - used when the customer picks
 * one option from a multipleMatches result returned by verifyVIN
 */
export const decodeSelectedPart = async (partId, customerName, shippingDate, vinNumber) => {
    const response = await fetch(`${API_URL}/decode`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ partId, customer: customerName, shippingDate })
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to decode part');
    }

    const data = await response.json();

    return {
        part_id: data.partId,
        model_year: extractYear(data.shippingDate),
        parts: data.parts,
        vin: vinNumber
    };
};

/**
 * Get parts is now included in verifyVIN response
 * This function is kept for compatibility but not used
 */
export const getParts = async (partId) => {
    // Parts are already returned by verifyVIN
    // This is a no-op for compatibility
    return { parts: [] };
};